import axios from '../axiosConfig'
import { getEnv } from '@utils/env'
import type { DashboardPeriod } from './finance.api'

// Constants
const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_DASHBOARD')

// Types
interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

export interface ChairTimePeriodParams {
  clinicId: string
  date: string
  period: DashboardPeriod
}

export interface ChairTimeOverviewResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  totalChairTime: string
  scheduledTime: string
  openChairTime: string
  efficiencyRate: number
  efficiencyGoal: number | null
  scheduledSlicePct: number
  availableSlicePct: number
  totalMinutes: number
  scheduledMinutes: number
  openMinutes: number
}

export interface ChairTimeDetailKpis {
  totalChairTime: string
  completedAppts: number
  scheduledTime: string
  openChairTime: string
  efficiencyRate: string
  cancellationRate: string
  comparisonLabel: string
  efficiencyDelta: string
  efficiencyTrend: 'up' | 'down'
  scheduledDelta: string
  scheduledTrend: 'up' | 'down'
  openDelta: string
  openTrend: 'up' | 'down'
  cancellationDelta: string
  cancellationTrend: 'up' | 'down'
}

export interface ChairTimeChartPoint {
  label: string
  scheduled: number
  open: number
}

export interface ChairTimeChartResult {
  data: ChairTimeChartPoint[]
  yAxisDomain: [number, number]
  yAxisTicks: number[]
  axisUnit: 'minutes' | 'hours'
}

export interface ChairTimeByProviderRow {
  id: string
  provider: string
  role: string
  totalHrs: string
  scheduledHrs: string
  openHrs: string
  efficiency: string
  completedAppts: number
  noShows: number
}

export interface ChairTimeByPeriodRow {
  id: string
  timePeriod: string
  totalHrs: string
  scheduledHrs: string
  openHrs: string
  efficiency: string
  completedAppts: number
  cancellations: number
  noShows: number
}

export interface ChairTimeDetailResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  kpis: ChairTimeDetailKpis
  chart: ChairTimeChartResult
  byProvider: ChairTimeByProviderRow[]
  byPeriod: ChairTimeByPeriodRow[]
}

// API functions
export const getChairTimeOverview = async (
  params: ChairTimePeriodParams,
  options?: { signal?: AbortSignal },
): Promise<ChairTimeOverviewResult> => {
  const response = await axios.get<ApiResponse<ChairTimeOverviewResult>>(
    `${BUSINESS_LAYER_API}/api/v1/dashboard/chair-time/overview`,
    { params, signal: options?.signal },
  )
  return response.data.data
}

export const getChairTimeDetail = async (
  params: ChairTimePeriodParams,
  options?: { signal?: AbortSignal },
): Promise<ChairTimeDetailResult> => {
  const response = await axios.get<ApiResponse<ChairTimeDetailResult>>(
    `${BUSINESS_LAYER_API}/api/v1/dashboard/chair-time/detail`,
    { params, signal: options?.signal },
  )
  return response.data.data
}
